import { useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";

import { AnalyticsPanel } from "../components/admin/AnalyticsPanel.js";
import { ProfilePanel } from "../components/admin/ProfilePanel.js";
import { BusinessHours } from "../components/admin/BusinessHours.js";
import { KnowledgePanel } from "../components/admin/KnowledgePanel.js";
import { PromptBuilder } from "../components/admin/PromptBuilder.js";
import { useSession } from "../session/SessionContext.js";

/**
 * The administrative area.
 *
 * Four screens behind three permissions, and the navigation only shows
 * what the signed-in person can actually reach. That is a courtesy rather than a
 * control — every route is enforced by the API regardless — but a menu offering a
 * page that answers 403 is a menu that teaches people to distrust it.
 *
 * A supervisor sees analytics and nothing else. Aggregate figures name no customer,
 * which is what makes them safe to show to whoever runs the team; the prompt builder
 * and the knowledge corpus decide what the assistant says to customers, and that is
 * an administrator's decision.
 */

interface AdminTab {
  readonly path: string;
  readonly label: string;
  /**
   * Null for a screen everybody may reach.
   *
   * Exactly one of these: your own account. Changing your own password is not an
   * administrative act, and a colleague with a narrow role must not be locked out of
   * it — the API applies the same rule, and takes the account from the session so
   * there is nothing here that could edit somebody else's.
   */
  readonly permission:
    "admin:assistant:read" | "admin:knowledge:read" | "admin:analytics:read" | null;
}

const TABS: readonly AdminTab[] = [
  { path: "profile", label: "Your account", permission: null },
  { path: "assistant", label: "Assistant", permission: "admin:assistant:read" },
  // Behind the assistant permission rather than one of its own: both decide what a
  // customer is told about the team, and a separate permission for "when are we
  // open" would be a distinction without a difference.
  { path: "hours", label: "Business hours", permission: "admin:assistant:read" },
  { path: "knowledge", label: "Knowledge", permission: "admin:knowledge:read" },
  { path: "analytics", label: "Analytics", permission: "admin:analytics:read" },
];

export function AdminView() {
  const { can, signOut, principal } = useSession();
  const [visible] = useState(() =>
    TABS.filter((tab) => tab.permission === null || can(tab.permission)),
  );

  /*
    There is no "you may not be here" state any more, and there cannot be: every
    agent can reach their own account, so `visible` always has at least one entry.
    The screen this replaces was correct while every tab needed a permission.
  */
  const first = visible[0]?.path ?? "profile";

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">Island Peptides Support</span>

        <nav className="row" style={{ gap: 4, marginLeft: 16 }}>
          <NavLink to="/conversations" className="tab">
            Queue
          </NavLink>
          {visible.map((tab) => (
            <NavLink key={tab.path} to={`/admin/${tab.path}`} className="tab">
              {tab.label}
            </NavLink>
          ))}
        </nav>

        <span className="spacer" />

        <span className="small muted">
          {principal?.type === "agent" ? `${principal.email} · ${principal.role}` : ""}
        </span>
        <button
          onClick={() => {
            void signOut();
          }}
        >
          Sign out
        </button>
      </header>

      <div className="admin-body">
        <Routes>
          <Route path="profile" element={<ProfilePanel />} />
          {can("admin:assistant:read") && <Route path="assistant" element={<PromptBuilder />} />}
          {can("admin:assistant:read") && <Route path="hours" element={<BusinessHours />} />}
          {can("admin:knowledge:read") && <Route path="knowledge" element={<KnowledgePanel />} />}
          {can("admin:analytics:read") && <Route path="analytics" element={<AnalyticsPanel />} />}
          <Route path="*" element={<Navigate to={`/admin/${first}`} replace />} />
        </Routes>
      </div>
    </div>
  );
}
